import { useEffect, useState, useCallback } from 'react'
import axios from 'axios'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

import { baseURL } from '../../config'

dayjs.extend(relativeTime)

function ExtensionAnalytics({ isDarkMode = true }) {
	const [analyticsData, setAnalyticsData] = useState(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)
	const [showBadActors, setShowBadActors] = useState(false)
	const [modalUser, setModalUser] = useState(null)

	const [page, setPage] = useState(1)
	const [limit] = useState(30)
	const [pagination, setPagination] = useState(null)
	const [search, setSearch] = useState('')
	const [searchInput, setSearchInput] = useState('')
	const [startDate, setStartDate] = useState(dayjs().subtract(7, 'day').format('YYYY-MM-DD'))
	const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'))
	const [badActorCount, setBadActorCount] = useState(0)

	const fetchData = useCallback(async () => {
		setLoading(true)
		try {
			const userToken = localStorage.getItem('token')
			const params = new URLSearchParams({
				page: page.toString(),
				limit: limit.toString(),
				startDate,
				endDate,
				badActorsOnly: showBadActors.toString(),
			})
			if (search) params.append('search', search)

			const response = await axios.get(
				`${baseURL}/api/admin/extension/analytics?${params}`,
				{ headers: { authorization: userToken } }
			)
			setAnalyticsData(response.data.data)
			setPagination(response.data.pagination)
			setBadActorCount(response.data.badActorCount || 0)
		} catch (err) {
			setError(err)
		} finally {
			setLoading(false)
		}
	}, [page, limit, search, startDate, endDate, showBadActors])

	useEffect(() => {
		fetchData()
	}, [fetchData])

	const handleSearch = () => {
		setPage(1)
		setSearch(searchInput)
	}

	const handleBadActorsToggle = () => {
		setPage(1)
		setShowBadActors(!showBadActors)
	}

	const cardStyle = isDarkMode
		? 'bg-gray-800 text-white'
		: 'bg-white text-gray-900 border border-gray-200'

	const modalBg = isDarkMode ? 'bg-gray-800' : 'bg-white border border-gray-200'
	const innerBg = isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
	const inputClass = isDarkMode
		? 'bg-gray-700 text-white'
		: 'bg-white text-gray-900 border border-gray-300'
	const textHeading = isDarkMode ? 'text-white' : 'text-gray-900'
	const textLabel = isDarkMode ? 'text-gray-400' : 'text-gray-500'
	const textFaint = isDarkMode ? 'text-gray-500' : 'text-gray-400'
	const textGreen = isDarkMode ? 'text-green-400' : 'text-green-600'
	const textYellow = isDarkMode ? 'text-yellow-400' : 'text-yellow-600'
	const textRed = isDarkMode ? 'text-red-400' : 'text-red-600'
	const closeBtn = isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'

	const getTotalRequests = analytics => {
		return analytics.reduce((sum, entry) => sum + entry.count, 0)
	}

	if (loading) {
		return <div className="text-center text-xl font-bold p-4">Loading...</div>
	}

	if (error) {
		return <div className="text-center text-red-500 p-4">Error: {error.message}</div>
	}

	return (
		<div className={`${isDarkMode ? 'bg-dark-color' : 'bg-white'} min-h-screen`}>
			<div className="p-4 space-y-3">
				<div className="flex flex-col sm:flex-row justify-between items-center gap-2">
					<h1 className={`text-xl md:text-2xl font-bold ${textHeading}`}>
						Extension Analytics ({pagination?.total || 0} users)
					</h1>
					<button
						onClick={handleBadActorsToggle}
						className={`px-4 py-2 rounded text-sm font-medium ${
							showBadActors
								? 'bg-red-600 hover:bg-red-700 text-white'
								: isDarkMode
									? 'bg-gray-600 hover:bg-gray-700 text-white'
									: 'bg-gray-200 hover:bg-gray-300 text-gray-700'
						}`}
					>
						{showBadActors ? 'Show All' : `Bad Actors (${badActorCount})`}
					</button>
				</div>

				<div className="flex flex-wrap gap-2 items-center">
					<input
						type="text"
						placeholder="Search by email..."
						value={searchInput}
						onChange={e => setSearchInput(e.target.value)}
						onKeyDown={e => e.key === 'Enter' && handleSearch()}
						className={`px-3 py-2 rounded ${inputClass} text-sm w-48`}
					/>
					<button
						onClick={handleSearch}
						className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm"
					>
						Search
					</button>
					<input
						type="date"
						value={startDate}
						onChange={e => { setStartDate(e.target.value); setPage(1) }}
						className={`px-3 py-2 rounded ${inputClass} text-sm`}
					/>
					<span className={textLabel}>to</span>
					<input
						type="date"
						value={endDate}
						onChange={e => { setEndDate(e.target.value); setPage(1) }}
						className={`px-3 py-2 rounded ${inputClass} text-sm`}
					/>
				</div>

				{pagination && (
					<div className="flex items-center gap-2">
						<button
							onClick={() => setPage(p => Math.max(1, p - 1))}
							disabled={page === 1}
							className={`px-3 py-1 ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'} disabled:opacity-50 disabled:cursor-not-allowed text-white rounded text-sm`}
						>
							Prev
						</button>
						<span className={`text-sm ${textLabel}`}>
							Page {pagination.page} of {pagination.totalPages}
						</span>
						<button
							onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
							disabled={page >= pagination.totalPages}
							className={`px-3 py-1 ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'} disabled:opacity-50 disabled:cursor-not-allowed text-white rounded text-sm`}
						>
							Next
						</button>
					</div>
				)}
			</div>

			<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-4 p-2 md:p-4">
				{analyticsData?.map((user, index) => (
					<div
						key={index}
						className={`${cardStyle} p-2 md:p-4 rounded-md shadow-md`}
					>
						<div className="flex justify-between items-start mb-2">
							<div className="flex-1 min-w-0">
								<h2 className="text-sm md:text-lg font-bold truncate">
									{user?.name || 'Unknown'}
								</h2>
								<p className={`text-xs md:text-sm ${textLabel} truncate`}>
									{user?.userEmail}
								</p>
							</div>
							<span
								className={`text-xs px-2 py-1 rounded ml-2 ${
									user?.subscription?.paid
										? 'bg-green-600'
										: user?.subscription?.isTrial
										? 'bg-yellow-600'
										: isDarkMode ? 'bg-gray-600' : 'bg-gray-400'
								}`}
							>
								{user?.subscription?.paid
									? 'paid'
									: user?.subscription?.isTrial
									? 'trial'
									: 'free'}
							</span>
						</div>

						<div className="grid grid-cols-2 gap-2 mb-2 text-xs md:text-sm">
							<div className={`${innerBg} p-2 rounded`}>
								<p className={textLabel}>Devices</p>
								<p className="font-bold">{user?.deviceCount || 0}</p>
							</div>
							<div className={`${innerBg} p-2 rounded`}>
								<p className={textLabel}>Requests</p>
								<p className="font-bold">{user?.totalClicks || getTotalRequests(user.analytics)}</p>
							</div>
						</div>

						<div className="mb-2">
							<p className={`text-xs ${textLabel} mb-1`}>Features Used:</p>
							<div className="flex flex-wrap gap-1">
								{user.analytics.slice(0, 5).map((entry, i) => (
									<span
										key={i}
										className={`text-xs ${innerBg} px-2 py-1 rounded truncate max-w-[120px]`}
										title={entry?.path}
									>
										{entry?.path?.split('/').pop() || entry?.path}
									</span>
								))}
								{user.analytics.length > 5 && (
									<span className={`text-xs ${textFaint}`}>
										+{user.analytics.length - 5} more
									</span>
								)}
							</div>
						</div>

						<button
							onClick={() => setModalUser(user)}
							className="w-full text-xs md:text-sm bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded"
						>
							View Details
						</button>
					</div>
				))}
			</div>

			{modalUser && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
					<div className={`${modalBg} rounded-lg p-4 md:p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto`}>
						<div className="flex justify-between items-start mb-4">
							<div>
								<h2 className={`text-lg md:text-xl font-bold ${textHeading}`}>
									{modalUser?.name || 'Unknown'}
								</h2>
								<p className={`${textLabel} text-sm`}>{modalUser?.userEmail}</p>
							</div>
							<button
								onClick={() => setModalUser(null)}
								className={`${closeBtn} text-2xl`}
							>
								×
							</button>
						</div>

						<div className="grid grid-cols-3 gap-2 mb-4">
							<div className={`${innerBg} p-3 rounded text-center`}>
								<p className={`${textLabel} text-xs`}>Devices</p>
								<p className={`${textHeading} text-lg font-bold`}>
									{modalUser?.deviceCount || 0}
								</p>
							</div>
							<div className={`${innerBg} p-3 rounded text-center`}>
								<p className={`${textLabel} text-xs`}>Requests</p>
								<p className={`${textHeading} text-lg font-bold`}>
									{modalUser?.totalClicks || getTotalRequests(modalUser.analytics)}
								</p>
							</div>
							<div className={`${innerBg} p-3 rounded text-center`}>
								<p className={`${textLabel} text-xs`}>Status</p>
								<p
									className={`text-lg font-bold ${
										modalUser?.subscription?.paid
											? textGreen
											: modalUser?.subscription?.isTrial
											? textYellow
											: textRed
									}`}
								>
									{modalUser?.subscription?.paid
										? 'Paid'
										: modalUser?.subscription?.isTrial
										? 'Trial'
										: 'Free'}
								</p>
							</div>
						</div>

						<h3 className={`${textHeading} font-semibold mb-2`}>
							All Features ({modalUser.analytics.length})
						</h3>
						<div className="space-y-2">
							{modalUser.analytics.map((entry, i) => (
								<div key={i} className={`${innerBg} p-3 rounded`}>
									<p className={`${textHeading} font-medium text-sm break-all`}>
										{entry?.path}
									</p>
									<div className={`flex flex-wrap gap-4 mt-2 text-xs ${textLabel}`}>
										<span>Count: <span className={textHeading}>{entry.count}</span></span>
										<span>Avg: <span className={textHeading}>{entry?.avgResponseTime?.toFixed(0)}ms</span></span>
										<span>IPs: <span className={textHeading}>{entry?.uniqueIPs?.length || 0}</span></span>
									</div>
									<p className={`${textFaint} text-xs mt-1`}>
										Last: {dayjs(entry.lastRequestAt).format('hh:mm A MMM DD YYYY')}
									</p>
								</div>
							))}
						</div>
					</div>
				</div>
			)}
		</div>
	)
}

export default ExtensionAnalytics
